import { BaseService } from "./base.service";
import { InviteGrantAccessPayload } from "../types/member-permission";
import api from "./api";

export const ALL_PERMISSIONS = ["read", "write", "delete", "update"];
// Must stay in sync with backend MemberModules
export const MEMBER_MODULES = [1, 2, 3, 4, 5, 6, 11, 14, 15];

class MemberPermissionService extends BaseService {
  constructor() {
    super("/member-permissions");
  }

  /**
   * Get all family member IDs this user can manage in the current family context.
   */
  async getManagedMemberIdsAndTokens(
    memberId?: string
  ): Promise<{ ids: string[]; tokens: any[] }> {
    let url = "/member-permissions/member-access";
    if (memberId) {
      url += `?memberId=${encodeURIComponent(memberId)}`;
    }
    const res = await api.get(url);
    return { ids: res.data.ids, tokens: res.data.tokens };
  }

  /**
   * Get pending access tokens where a member is requesting access to other profiles.
   * This is the reverse of getManagedMemberIdsAndTokens - it finds tokens where
   * the member is being granted access (token_params.familyMemberId = memberId).
   */
  async getPendingAccessTokensForMember(
    memberId: string
  ): Promise<{ tokens: any[] }> {
    const url = `/member-permissions/member-requesting-access?memberId=${encodeURIComponent(
      memberId
    )}`;
    const res = await api.get(url);
    return { tokens: res.data.tokens };
  }

  async getManagedMemberIds(): Promise<string[]> {
    const res = await api.get("/member-permissions/member-access");
    return res.data;
  }

  async getManagedMembers(): Promise<any[]> {
    const res = await api.get("/members/managed-members");
    return Array.isArray(res.data) ? res.data : [];
  }

  async inviteGrantAccess(payload: InviteGrantAccessPayload) {
    return this.post("/invite-grant-access", payload);
  }

  async setPermissions(payload: {
    memberId: string;
    familyId: string;
    familyMemberId: string;
    permissions: Record<string, string[]>;
    roleId?: string;
    skipTokenDelete?: boolean;
  }) {
    return this.post("/accept-grant-access", payload);
  }

  async acceptGrantAccess({
    memberId,
    familyId,
    familyMemberId,
    roleId,
    skipTokenDelete,
  }: {
    memberId: string;
    familyId: string;
    familyMemberId: string;
    roleId?: string;
    skipTokenDelete?: boolean;
  }) {
    const permissionsPayload: Record<string, string[]> = {};
    MEMBER_MODULES.forEach((m) => {
      permissionsPayload[m] = ALL_PERMISSIONS;
    });
    return this.setPermissions({
      memberId,
      familyId,
      familyMemberId,
      permissions: permissionsPayload,
      roleId,
      skipTokenDelete,
    });
  }

  async declineGrantAccess({
    tokenData,
    invitedMember,
    managedMember,
  }: {
    tokenData: any;
    invitedMember: any;
    managedMember: any;
  }) {
    const tokenType = tokenData.token_params?.tokenType || "GRANT_ACCESS";
    const familyId = tokenData.family_id;
    const memberId = invitedMember.id;
    const familyMemberId = managedMember.id;
    return this.denyConsent({ tokenType, familyId, memberId, familyMemberId });
  }

  async denyConsent(payload: {
    tokenType: string;
    familyId: string;
    memberId: string;
    familyMemberId: string;
  }) {
    return this.post("/deny-grant-access", payload);
  }

  async revokeAccess(payload: {
    memberId: string;
    familyId: string;
    familyMemberId: string;
  }) {
    return this.post("/revoke-access", payload);
  }

  async revokeInvite(payload: {
    memberId: string;
    familyId: string;
    familyMemberId: string;
    token?: string;
  }) {
    return this.post("/revoke-invite", payload);
  }

  async requestAccess(payload: {
    memberId: string;
    familyId: string;
    familyMemberId: string;
  }) {
    return this.post("/request-access", payload);
  }

  async updateNickname({
    memberId,
    familyId,
    familyMemberId,
    ...rest
  }: {
    memberId: string;
    familyId: string;
    familyMemberId: string;
    [key: string]: any;
  }) {
    // PUT /member-permissions/:familyMemberId with nickname or other fields
    return this.put(`/${memberId}`, {
      memberId,
      familyMemberId,
      familyId,
      ...rest,
    });
  }
}

export const memberPermissionService = new MemberPermissionService();
export default memberPermissionService;
